/**
 * TopNav, Salesforce Lightning layout per Ean's 2026-06-03 feedback wave (P5).
 *
 *   [Olytic logo + "Olytic" name] | [Home | Connectors | Catalog]
 *                                       ----- left half -----
 *                                                    [Workspace name | Help | Gear or Back-to-app | Avatar]
 *                                                    ----- right half -----
 *
 * - Far left: Olytic platform branding always primary (logo + wordmark).
 * - Vertical divider separates brand from nav tabs.
 * - Nav tabs immediately after the divider (still on the left half of the bar).
 * - Right cluster: workspace name → help icon (dropdown) → gear (user mode)
 *   or back-to-app icon (admin mode) → user avatar.
 *
 * Help dropdown (everyone sees both — consolidation 2026-06-10):
 *   - "Contact Your Admin" → opens RequestModal destination='admin'
 *   - "Contact Olytic"                   → opens RequestModal destination='olytic'
 * Inside the Olytic modal, the "Contact Olytic Support" sub-option is
 * gated to owner+admin; the other two (Request/Suggest + Professional
 * Services) are open to everyone.
 *
 * Standard users (role='user') never see the gear icon.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useNavigate } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import { signOut } from '../lib/api.ts';
import { formatLabel } from '../lib/format.ts';
import { GlobalSearch } from './GlobalSearch.tsx';
import { IconChevron } from './Icon.tsx';
import { NotificationBell } from './NotificationBell.tsx';
import { ObjectIcon } from './ObjectIcon.tsx';
import { type RequestDestination, RequestModal } from './RequestModal.tsx';

// Push 5-post-smoke-28 Sub-agent P (2026-07-15) — keyboard shortcuts
// removed app-wide per Ean's directive: "Get rid of all keyboard
// shortcuts for now across the entire application, they feel weird and
// dev coded." KeyboardShortcutsHelp import + modal wiring dropped;
// Cmd/Ctrl+K palette-open handler removed; `?` help-open handler
// removed; visible kbd tile removed from the search chip.

interface TopNavProps {
  /** When true, render "Back to App" instead of the gear icon. */
  mode: 'user' | 'admin';
}

// Push #82.7-followup 2026-06-19 — retired buildSupportMailto() helper
// alongside the "Email support" dropdown item per Ean's directive: no
// user-facing email surfaces. The in-app "Contact Olytic" form (opened
// via openContactOlytic in the Help dropdown) covers the support path
// and never exposes an email address to the user.

export function TopNav({ mode }: TopNavProps) {
  // Sub-agent IIIII (2026-07-29) — Track 3: broadcastSignOut so other
  // tabs flip to unauthenticated as soon as this tab signs out. No
  // navigation happens in the other tabs' provider directly (we're
  // above the router there); the shell's session-status check picks
  // up the change on the next render and issues its own redirect.
  const { user, refresh, broadcastSignOut } = useSession();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [requestModal, setRequestModal] = useState<{
    open: boolean;
    destination: RequestDestination;
  }>({ open: false, destination: 'olytic' });
  // Push #17 — global search palette. Push 5-post-smoke-28 dropped the
  // Cmd-K keyboard-shortcut open handler; palette opens only via the
  // TopNav search chip click.
  const [searchOpen, setSearchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const helpRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(e.target as Node)) {
        setHelpOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Push 5-post-smoke-28 Sub-agent P (2026-07-15) — global keyboard
  // shortcut handlers removed per Ean's directive. Cmd/Ctrl+K palette
  // open, `?` help modal open — both dropped. Palette opens via the
  // TopNav search chip click only.
  // Sub-agent TTTT (2026-07-29) — Track 1(c) event bridge. The
  // collapsed left chat rail's "Search chats" sliver dispatches
  // `olytic:global-search:open` when clicked so the palette can open
  // WITHOUT expanding the rail (per Ean's "floating command palette
  // without expanding the rail" directive). Cheaper than lifting
  // `searchOpen` into a context: cross-tree signaling via a documented
  // window event, listener lives here in the palette's owner.
  useEffect(() => {
    function onOpenPalette() {
      setSearchOpen(true);
    }
    window.addEventListener('olytic:global-search:open', onOpenPalette);
    return () => {
      window.removeEventListener('olytic:global-search:open', onOpenPalette);
    };
  }, []);

  // M1.5 Wave E / E3 — mobile drawer toggle. Hamburger lives in TopNav
  // at <720px viewports. Click sets data-mobile-nav-open="true" on
  // <body>; CSS uses that attribute to slide the appropriate drawer in
  // (AdminSubNav in admin mode; the topnav-nav links in user mode via
  // an attribute-driven CSS reveal). Closes on any nav-link click via
  // the global listener below + on backdrop click via the overlay's
  // own onClick. Functional, not pretty — no animation library, just
  // a CSS transform transition.
  function closeMobileNav() {
    if (typeof document !== 'undefined') {
      document.body.removeAttribute('data-mobile-nav-open');
    }
  }
  function toggleMobileNav() {
    if (typeof document === 'undefined') return;
    if (document.body.hasAttribute('data-mobile-nav-open')) {
      document.body.removeAttribute('data-mobile-nav-open');
    } else {
      document.body.setAttribute('data-mobile-nav-open', 'true');
    }
  }
  // Close the drawer whenever the route changes — any NavLink click
  // inside the drawer will navigate, which fires the listener below.
  // Cheaper than wiring onClick to every NavLink individually.
  useEffect(() => {
    function onNavClick(e: MouseEvent) {
      const target = e.target as HTMLElement | null;
      if (target === null) return;
      const link = target.closest('a');
      if (link === null) return;
      closeMobileNav();
    }
    document.addEventListener('click', onNavClick);
    return () => document.removeEventListener('click', onNavClick);
  }, []);
  // Defensive cleanup so navigating between admin/user modes doesn't
  // leave the body attribute hanging.
  useEffect(() => {
    return () => closeMobileNav();
  }, []);

  if (user === null) return null;

  const initials = user.name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const canAccessAdmin = user.role === 'owner' || user.role === 'admin';

  async function handleSignOut() {
    // Push #88 (preempt G): capture the email BEFORE signOut() clears the
    // session, so we can land on /sign-in?email=... with the field
    // pre-filled. Saves a typed-it-twice friction step on return visits.
    // Non-null assertion is safe: the early return at line 125 guarantees
    // user is non-null whenever this handler runs (rendering is gated).
    const emailHint = user!.email;
    try {
      await signOut();
    } catch {
      // ignore
    }
    // Sub-agent IIIII (2026-07-29) — Track 3: signal every other open
    // tab that the session died so they redirect to /sign-in on their
    // own render. Best-effort; safe if BroadcastChannel is absent.
    broadcastSignOut();
    refresh();
    const signInPath =
      emailHint !== '' && emailHint !== undefined
        ? `/sign-in?email=${encodeURIComponent(emailHint)}`
        : '/sign-in';
    navigate(signInPath);
  }

  function openContactAdmin() {
    setHelpOpen(false);
    setRequestModal({ open: true, destination: 'admin' });
  }

  function openContactOlytic() {
    setHelpOpen(false);
    setRequestModal({ open: true, destination: 'olytic' });
  }

  return (
    <>
      <nav className="topnav">
        {/* LEFT GROUP, Olytic brand → divider → nav tabs. All flush-left. */}
        <div className="topnav-left">
          {/* M1.5 Wave E / E3. mobile hamburger. Hidden by CSS at >720px.
              Toggles the data-mobile-nav-open attribute on <body>; CSS
              picks up the attribute and slides the appropriate drawer
              in (admin sub-nav in admin mode, user nav-link list in
              user mode). */}
          <button
            type="button"
            className="topnav-hamburger"
            onClick={toggleMobileNav}
            aria-label="Toggle navigation"
            title="Toggle navigation"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M2 4h14" />
              <path d="M2 9h14" />
              <path d="M2 14h14" />
            </svg>
          </button>
          <Link
            to={mode === 'admin' ? '/admin' : '/home'}
            className="topnav-brand"
            aria-label={user?.brand_logo_r2_key ? user.account_name : 'Olytic'}
          >
            <WorkspaceBrandLogo
              accountId={user?.account_id ?? null}
              logoR2Key={user?.brand_logo_r2_key ?? null}
              logoUpdatedAt={user?.brand_logo_updated_at ?? null}
              accountName={user?.account_name ?? null}
            />
            {/* Wordmark shows Olytic by default; when a workspace uploads
                their own logo the wordmark swaps to the workspace name so
                the brand block reads coherently. The right-cluster
                "Workspace: <name>" pill still surfaces the workspace name
                for teams without a custom logo. */}
            <span className="topnav-brand-name">
              {user?.brand_logo_r2_key && user?.account_name ? user.account_name : 'Olytic'}
            </span>
          </Link>

          {mode === 'user' && (
            <>
              <span className="topnav-nav-separator" aria-hidden="true" />
              {/* IA per OC adjudication 2026-06-09 (Push #3.2.5). three
                  visual groups separated by faint dividers so the mental
                  model reads at a glance:
                    • Group A (workspace surfaces)  → Chat · Overview
                    • Group B (pillar surfaces)     → Models · Connectors · Prompts
                    • Group C (platform + ops)      → Catalog · Activity
                  Each group's items share rhythm; the dividers signal
                  category boundaries without shouting (subtle vertical
                  line matching the brand/nav divider). Tooltips clarify
                  each pillar's purpose for first-touch users.
                  D-167: pillar order is Models → Connectors → Prompts. */}
              {/* Push #16. nav bars stay text-only per icon discipline rule
                 #1. The page-level Lightning highlight tile + content cards
                 carry the iconography; nav bars don't need it. Reduces noise
                 across both modes. */}
              <div className="topnav-nav" role="navigation" aria-label="User navigation">
                <NavLink
                  to="/chat"
                  title="Ask your AI anything"
                  className={({ isActive }) => `topnav-nav-link${isActive ? ' active' : ''}`}
                >
                  Chat
                </NavLink>
                <NavLink
                  to="/home"
                  end
                  title="Workspace home. Orient yourself + getting started"
                  className={({ isActive }) => `topnav-nav-link${isActive ? ' active' : ''}`}
                >
                  Home
                </NavLink>
                <span className="topnav-nav-group-sep" aria-hidden="true" />
                {/* Push 4-0 (2026-07-01) — Capabilities dropdown groups the
                   4 capability pillars (Models / Connectors / Prompts /
                   Documents) under one nav entry to preserve top-bar
                   real estate as new capabilities land (agents,
                   orchestrations). D-167 pillar order preserved inside
                   the dropdown. Uses a details/summary pair styled to
                   match the nav — no useState needed, native collapse
                   semantics + click-outside handled by browser. */}
                <CapabilitiesDropdown />
                <span className="topnav-nav-group-sep" aria-hidden="true" />
                <NavLink
                  to="/market"
                  title="Browse everything Olytic offers"
                  className={({ isActive }) => `topnav-nav-link${isActive ? ' active' : ''}`}
                >
                  Market
                </NavLink>
                {/* Activity removed from user nav per Ean 2026-06-09: users
                   live in Chat, may never (or be unable to) flip into admin
                   mode, and the link previously routed to /admin/activity
                   which is admin-only. Chat history surfaces in Chat itself
                   (sidebar. Push #3.3 follow-up). Activity-for-admins still
                   exists under /admin/activity via the gear flip; D-153
                   principle: user-mode surfaces show user-mode things only. */}
              </div>
            </>
          )}
        </div>

        {/* RIGHT, search · workspace name · help · gear/back · avatar. */}
        <div className="topnav-right">
          {/* Push #17. global search chip. Push 5-post-smoke-28 dropped
              the visible ⌘K kbd tile AND the Cmd-K keyboard-shortcut open
              handler per Ean's app-wide "remove all keyboard shortcuts"
              directive. Chip stays clickable — it's the only way to open
              the palette now. */}
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="topnav-search-chip"
            title="Search"
            aria-label="Open command palette"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              style={{ flexShrink: 0 }}
            >
              <circle cx="7" cy="7" r="4" />
              <path d="M10 10l3 3" />
            </svg>
            <span style={{ flex: 1, textAlign: 'left' }}>Search…</span>
          </button>

          <div className="topnav-workspace" title={`${user.account_name} workspace`}>
            <span className="topnav-workspace-label">Workspace</span>
            <span className="topnav-workspace-name">{user.account_name}</span>
          </div>

          {/* Push #82.6. notification bell. Reads /api/me/notifications,
              polls 30s, badge shows unread count, dropdown shows last 20. */}
          <NotificationBell />

          {/* HELP, opens role-aware dropdown of request shortcuts. */}
          <div className="topnav-user" ref={helpRef}>
            <button
              type="button"
              className="topnav-helpbtn"
              onClick={() => setHelpOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={helpOpen}
              aria-label="Help and feedback"
              title="Help and feedback"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {/* Push #35. bumped circle r 7.5 → 8.6 so the help icon's
                   optical mass matches the gear's near-edge teeth. Prior
                   sizing left ~1.7px of empty visual margin on each side
                   that read as a wider help→gear gap even though the flex
                   `gap: 14px` is uniform edge-to-edge. */}
                <circle cx="10" cy="10" r="8.6" />
                <path d="M7.4 7.6a2.6 2.6 0 1 1 3.6 2.4c-.7.3-1 .8-1 1.5v.2" />
                <circle cx="10" cy="14.6" r="0.65" fill="currentColor" stroke="none" />
              </svg>
            </button>
            {helpOpen && (
              <div className="topnav-dropdown" role="menu">
                <button
                  type="button"
                  className="topnav-dropdown-item"
                  onClick={openContactAdmin}
                  role="menuitem"
                >
                  Contact Your Admin
                </button>
                <button
                  type="button"
                  className="topnav-dropdown-item"
                  onClick={openContactOlytic}
                  role="menuitem"
                >
                  Contact Olytic
                </button>
                {/* Push #82.7-followup 2026-06-19. removed "Email support"
                   mailto per Ean's directive. Users use the "Contact Olytic"
                   item above which opens the in-app form (no email visible). */}
              </div>
            )}
          </div>

          {mode === 'admin' ? (
            <NavLink
              to="/home"
              className="topnav-mode-toggle topnav-gear"
              title="Back to app"
              aria-label="Back to user mode"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <rect x="2.5" y="2.5" width="6" height="6" rx="1.2" />
                <rect x="11.5" y="2.5" width="6" height="6" rx="1.2" />
                <rect x="2.5" y="11.5" width="6" height="6" rx="1.2" />
                <rect x="11.5" y="11.5" width="6" height="6" rx="1.2" />
              </svg>
            </NavLink>
          ) : canAccessAdmin ? (
            <NavLink
              to="/admin"
              className="topnav-mode-toggle topnav-gear"
              title="Admin settings"
              aria-label="Switch to admin mode"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {/* Heroicons cog-6-tooth. rotationally symmetric six-tooth gear.
                   Replaced the hand-rolled lopsided path on 2026-06-10. */}
                <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.213-1.281z" />
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </NavLink>
          ) : null}

          <div className="topnav-user" ref={dropdownRef}>
            <button
              type="button"
              className="topnav-avatar"
              onClick={() => setDropdownOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={dropdownOpen}
              aria-label="User menu"
            >
              {/* Push 80 (2026-07-23) — render the uploaded avatar photo
                 when the user has one; fall back to initials otherwise.
                 Cache-buster hangs off avatar_updated_at (the DB column
                 bumped on every successful upload) so a fresh crop
                 supersedes any browser cache immediately. patchSession()
                 on the Photo card save merges the new avatar_updated_at
                 into the session; React re-renders this TopNav with a
                 different src → image swap is instant. onError falls
                 back to the initials chip if the R2 proxy 404s (rare —
                 the r2_key column is set atomically with the object
                 upload). */}
              <TopNavAvatarImage
                userId={user.user_id}
                avatarKey={user.avatar_r2_object_key}
                updatedAt={user.avatar_updated_at}
                initials={initials}
              />
            </button>
            {dropdownOpen && (
              <div className="topnav-dropdown" role="menu">
                <div className="topnav-dropdown-header">
                  <div className="topnav-dropdown-name">{user.name}</div>
                  <div className="topnav-dropdown-email">{user.email}</div>
                  <div className="topnav-dropdown-role">{formatLabel(user.role)}</div>
                </div>
                <div className="topnav-dropdown-divider" />
                {/* Push 4-1e (2026-07-07): gear dropdown restructured. New
                   items (Settings · My activity · Legal & policies) replace
                   the prior Profile · Privacy · Terms · Acceptable use rows.
                   Profile becomes a tab under /settings; the three legal
                   pages consolidate into a single /legal tabbed surface. */}
                <Link
                  to="/settings"
                  className="topnav-dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                  role="menuitem"
                >
                  Settings
                </Link>
                <Link
                  to="/activity"
                  className="topnav-dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                  role="menuitem"
                >
                  My activity
                </Link>
                <div className="topnav-dropdown-divider" />
                <Link
                  to="/legal"
                  className="topnav-dropdown-item"
                  onClick={() => setDropdownOpen(false)}
                  role="menuitem"
                >
                  Legal &amp; policies
                </Link>
                <div className="topnav-dropdown-divider" />
                <button
                  type="button"
                  className="topnav-dropdown-item topnav-dropdown-signout"
                  onClick={handleSignOut}
                  role="menuitem"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* M1.5 Wave E / E3. mobile drawer backdrop. Always rendered;
          CSS makes it visible only when body[data-mobile-nav-open].
          Click closes the drawer. Sits below the drawer but above
          page content via z-index. */}
      <div className="topnav-mobile-backdrop" onClick={closeMobileNav} aria-hidden="true" />

      <RequestModal
        open={requestModal.open}
        onClose={() => setRequestModal((s) => ({ ...s, open: false }))}
        destination={requestModal.destination}
      />

      {/* Push #17. global search modal. Scope follows TopNav mode.
         Push 5-post-smoke-28 dropped the KeyboardShortcutsHelp modal
         (removed app-wide) and the associated onOpenKeyboardHelp prop. */}
      <GlobalSearch
        open={searchOpen}
        scope={mode === 'admin' ? 'admin' : 'user'}
        onClose={() => setSearchOpen(false)}
      />
    </>
  );
}

/**
 * Push 4-0 (2026-07-01) → Push 4-1d-h (2026-07-02 rebuild) — Capabilities
 * dropdown. Salesforce-Lightning-style controlled dropdown replacing the
 * prior native <details>/<summary> which had two documented UX pitfalls:
 * (1) native disclosure-widget semantics don't close on outside click,
 * (2) DOM `open` state doesn't reset on route change so the menu stayed
 * stuck open after navigating. Ean smoke walk 2026-07-02 flagged both.
 *
 * New shape (mirrors KebabMenu.tsx pattern):
 *   - useState-controlled `open`.
 *   - Click-outside: mousedown listener on document while open.
 *   - Escape key: keydown listener while open.
 *   - Click NavLink item: closes + navigates.
 *   - Chevron: proper SVG (currentColor) rotating 180° when open.
 *
 * Grouped children stay in D-167 pillar order: Models → Connectors →
 * Prompts → Library.
 */
/**
 * Push 4-1d-l (2026-07-02) — 3rd rebuild. The controlled-useState-with-
 * absolute-positioned-menu shape from Push 4-1d-h did not survive real
 * TopNav rendering: the absolute-positioned menu got clipped or hidden by
 * an ancestor's overflow/z-index (Ean smoke walk report). Same failure
 * mode Push #48 fix documented for KebabMenu — resolved there with
 * `createPortal` + `position: fixed` so the menu escapes overflow/z-index
 * of all ancestors. Reusing the exact KebabMenu pattern here so the
 * dropdown actually renders, and polishing chrome to Salesforce Lightning
 * enterprise-cleanliness bar.
 *
 * Shape (mirrors KebabMenu.tsx):
 *   - useState(open) toggled on trigger click
 *   - Trigger's bounding rect computed via useLayoutEffect
 *   - Menu portal-rendered to document.body with position: fixed
 *   - Recomputed on scroll/resize while open
 *   - mousedown listener on document closes on click-outside
 *   - Escape key closes
 *   - Click NavLink item closes + navigates
 *   - Chevron rotates 180° via CSS class
 */
const CAPABILITIES_MENU_MIN_WIDTH = 200;
const CAPABILITIES_MENU_GAP = 6;

interface CapabilitiesMenuRect {
  top: number;
  left: number;
}

function CapabilitiesDropdown() {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<CapabilitiesMenuRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  function computeRect(): CapabilitiesMenuRect | null {
    const btn = triggerRef.current;
    if (btn === null) return null;
    const r = btn.getBoundingClientRect();
    // Left-align to the trigger; keep at least 8px from viewport edges.
    let left = r.left;
    if (left + CAPABILITIES_MENU_MIN_WIDTH > window.innerWidth - 8) {
      left = window.innerWidth - CAPABILITIES_MENU_MIN_WIDTH - 8;
    }
    if (left < 8) left = 8;
    return { top: r.bottom + CAPABILITIES_MENU_GAP, left };
  }

  useLayoutEffect(() => {
    if (!open) return;
    setRect(computeRect());
    function handle() {
      setRect(computeRect());
    }
    window.addEventListener('scroll', handle, true);
    window.addEventListener('resize', handle);
    return () => {
      window.removeEventListener('scroll', handle, true);
      window.removeEventListener('resize', handle);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`topnav-nav-link capabilities-dropdown-trigger${open ? ' is-open' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Your workspace's capabilities — models, connectors, prompts, library"
      >
        Capabilities
        <span className={`capabilities-dropdown-caret${open ? ' open' : ''}`} aria-hidden="true">
          {/* Push 5-post-smoke-25a (2026-07-14) — inline chevron SVG
              replaced with IconChevron primitive so the caret shares
              canonical geometry with the rest of the app. The wrapper
              span's CSS rotates 180deg when open — inline SVG carries
              no direction override so the CSS class stays in charge. */}
          <IconChevron size={10} />
        </span>
      </button>
      {open &&
        rect !== null &&
        createPortal(
          <div
            ref={menuRef}
            className="capabilities-dropdown-menu"
            role="menu"
            style={{ position: 'fixed', top: rect.top, left: rect.left }}
          >
            {/* Push 4-1n (2026-07-09): Removed "CAPABILITIES" caption per Ean
                nitpick. The trigger button already says "Capabilities" —
                restating it inside the menu is redundant. The four items are
                visually grouped by the popover chrome itself. */}
            <NavLink
              to="/models"
              className={({ isActive }) => `capabilities-dropdown-item${isActive ? ' active' : ''}`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <ObjectIcon kind="models" size="xs" />
              <span className="capabilities-dropdown-item-label">Models</span>
            </NavLink>
            <NavLink
              to="/connectors"
              className={({ isActive }) => `capabilities-dropdown-item${isActive ? ' active' : ''}`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <ObjectIcon kind="connectors" size="xs" />
              <span className="capabilities-dropdown-item-label">Connectors</span>
            </NavLink>
            <NavLink
              to="/prompts"
              className={({ isActive }) => `capabilities-dropdown-item${isActive ? ' active' : ''}`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <ObjectIcon kind="prompts" size="xs" />
              <span className="capabilities-dropdown-item-label">Prompts</span>
            </NavLink>
            <NavLink
              to="/library"
              className={({ isActive }) => `capabilities-dropdown-item${isActive ? ' active' : ''}`}
              role="menuitem"
              onClick={() => setOpen(false)}
            >
              <ObjectIcon kind="library" size="xs" />
              <span className="capabilities-dropdown-item-label">Library</span>
            </NavLink>
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Push 80 (2026-07-23) — user profile photo for the TopNav avatar chip.
 *
 * Renders the uploaded photo from the R2 proxy when the user has one;
 * falls back to the initials chip otherwise. Same shape as the
 * WorkspaceBrandLogo below — deterministic cache-busting via the
 * DB-bumped avatar_updated_at column so a fresh crop supersedes any
 * browser cache immediately.
 *
 * Also subscribes to the `olytic:avatar-changed` custom event fired by
 * ProfileTab on save. patchSession() already re-renders this component
 * through the session context; the extra event is defense-in-depth for
 * any cross-tab or cross-surface path where session state might not
 * carry the update (e.g., admin surfaces mutating another user's
 * avatar — not shipping today but the pattern's cheap to include).
 *
 * onError: if the proxy 404s (e.g., transient R2 outage), fall back to
 * the initials chip rather than rendering a broken image icon.
 */
function TopNavAvatarImage({
  userId,
  avatarKey,
  updatedAt,
  initials,
}: {
  userId: string;
  avatarKey: string | null;
  updatedAt: string | null;
  initials: string;
}) {
  const [errored, setErrored] = useState(false);
  const [bump, setBump] = useState(0);
  useEffect(() => {
    function onChanged() {
      // Retry: photo may have changed on another surface (or the same
      // surface via patchSession, which already re-renders us). Clear
      // the error state and bump the cache-buster so <img> re-fetches.
      setErrored(false);
      setBump((b) => b + 1);
    }
    window.addEventListener('olytic:avatar-changed', onChanged);
    return () => {
      window.removeEventListener('olytic:avatar-changed', onChanged);
    };
  }, []);
  // Reset error on avatarKey / updatedAt change so a new upload
  // triggers a fresh fetch even if the last one 404'd.
  useEffect(() => {
    setErrored(false);
  }, [avatarKey, updatedAt]);
  const shouldRenderImage = avatarKey !== null && avatarKey !== '' && !errored;
  if (!shouldRenderImage) {
    return <>{initials}</>;
  }
  const cacheBuster = `${updatedAt ?? '1'}-${bump}`;
  return (
    <img
      src={`/avatar/${userId}?v=${encodeURIComponent(cacheBuster)}`}
      alt=""
      aria-hidden="true"
      className="topnav-avatar-image"
      onError={() => setErrored(true)}
    />
  );
}

/**
 * Push 5-post-smoke-43 (Sub-agent EE, 2026-07-15) — workspace brand logo
 * for the TopNav brand block. Falls back to /olytic-mark.png when the
 * workspace hasn't uploaded a custom logo. onError fallback catches the
 * (rare) case where the R2 key is set but the object is missing —
 * degrades gracefully to the default mark rather than a broken image.
 */
function WorkspaceBrandLogo({
  accountId,
  logoR2Key,
  logoUpdatedAt,
  accountName,
}: {
  accountId: string | null;
  logoR2Key: string | null;
  logoUpdatedAt: string | null;
  accountName: string | null;
}) {
  const useCustom = accountId !== null && logoR2Key !== null && logoR2Key !== '';
  const src = useCustom
    ? `/workspace-logo/${accountId}?v=${encodeURIComponent(logoUpdatedAt ?? '1')}`
    : '/olytic-mark.png';
  return (
    <img
      src={src}
      alt={useCustom && accountName ? `${accountName} logo` : ''}
      aria-hidden={!useCustom}
      className="topnav-brand-logo"
      width={28}
      height={28}
      onError={(e) => {
        // Custom logo failed to load — fall back to the default mark so
        // the brand block never renders as a broken image icon.
        if (e.currentTarget.src.includes('/workspace-logo/')) {
          e.currentTarget.src = '/olytic-mark.png';
        }
      }}
    />
  );
}
